// TEMPORARY: fake restroom data for testing the UI with a live game.
// On with the dashboard-wide TEST_MODE (src/testMode.js), or by itself with DUMMY_ENABLED below.
// It only ever runs under `npm start` (development); production builds can never enable it.
// Remove this file (and its uses in FacilitiesLocationController.js, facilitiesApi.js,
// LocationsController.js and the banner in FacilitiesLocation.js) when done.
//
// Shares the test game (gates 2 hours, start 1 hour before page load, three past games). Sensors
// report every 30 seconds: queues come back as 30-second readings, entries/exits one point per
// minute (the v2 route groups them by minute). Uses the location's real restrooms (names, images,
// queue sensors), in the v2 route shapes. Each restroom steps through the edge cases below in
// 12-minute windows, on top of a pregame rush, waves through the game and a big break midway.
import { getQueuesAPI } from '../../util.js';
import {
  IS_DEV, USE_TEST_MODE, noise, testSchedule, gameContext, readingTimes, localStamp, edgeCase, edgeProgress, crowdCurve,
} from '../../testMode';

const DUMMY_ENABLED = false;

export const USE_DUMMY_FACILITIES_DATA = IS_DEV && (DUMMY_ENABLED || USE_TEST_MODE);

const QUEUE_CASES = ['normal', 'exact10', 'nine', 'spike', 'minimum', 'rising', 'falling', 'normal'];
// Entries: a rush pushes restrooms toward (and past) their next cleaning; 'stopped' means no one comes in
const FLOW_CASES = ['normal', 'rush', 'normal', 'quiet', 'normal', 'stopped', 'rush'];

// Restroom character: how busy it is and how quickly it queues (women's restrooms queue more)
const traits = (restroom, index) => {
  const label = String(restroom.label || '').toLowerCase();
  const womens = /wom[ae]n|ladies|female/.test(label);
  const family = /family|companion|unisex|accessible/.test(label);
  return {
    weight: (family ? 0.35 : 1) * (1.35 - (index % 5) * 0.15 + noise(index + 3) * 0.3),
    queueiness: family ? 0.5 : womens ? 1.6 : 1,
    hasQueue: !!restroom.multiSensor?.queue_time,
  };
};

// Entries per minute to one restroom
const entryRate = (ctx, m, t, i) => {
  const c = edgeCase(FLOW_CASES, i, m);
  if (c === 'stopped') return 0;
  const base = (0.1 + crowdCurve(ctx, m) * 3) * t.weight * ctx.scale;
  return c === 'quiet' ? base * 0.15 : c === 'rush' ? base * 2.5 : base;
};

// One restroom's queue at a reading, or null while its queue sensor is stale (whole minutes, never below 1, like the backend)
const queueReading = (ctx, sec, i, t) => {
  const m = Math.floor(sec / 60);
  const c = edgeCase(QUEUE_CASES, i, m);
  if (c === 'stale') return null;
  const p = edgeProgress(m);
  const n = noise(sec / 30 + i * 131 + ctx.seed);
  const base = 1 + crowdCurve(ctx, m) * 8 * t.queueiness * ctx.scale + n * 1.5;
  const v = {
    exact10: 10, nine: 9, spike: 22 + n * 6, minimum: 1, rising: 3 + p * 1.2, falling: 16 - p * 1.2,
  }[c] ?? base;
  return Math.max(1, Math.round(v));
};

// 'get-facilities-restrooms-location-daily-data-v2':
// { restroomId: { totalEntrances, totalExits, data: [{ timestamp, countIn, countOut }], queueData } }
const buildDaily = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  const restrooms = currentLocation.restrooms || [];
  const out = {};
  restrooms.forEach((restroom, i) => {
    if (!ctx) {
      out[String(restroom.restroomId)] = { totalEntrances: 0, totalExits: 0, data: [], queueData: [] };
      return;
    }
    const t = traits(restroom, i);
    let cin = 0;
    let cout = 0;
    const data = [];
    const queueData = [];
    readingTimes(ctx, ctx.gates - 45).forEach((sec, k) => {
      const m = Math.floor(sec / 60);
      if (k > 0) {
        const n = noise(sec / 30 + i * 131 + ctx.seed);
        cin += Math.round((entryRate(ctx, m, t, i) / 2) * (0.5 + n));
        // Exits trail entries by a few minutes
        cout = Math.max(cout, cin - Math.round(3 + noise(m * 13 + i) * 4));
      }
      // The route returns one point per minute (the later reading wins)
      if (sec % 60 === 30 || sec === ctx.endSec) {
        const stamp = localStamp(dateStr, m * 60);
        if (data.length && data[data.length - 1].timestamp === stamp) data.pop();
        data.push({ timestamp: stamp, countIn: cin, countOut: cout });
      }
      if (t.hasQueue && m >= ctx.gates - 10) {
        const q = queueReading(ctx, sec, i, t);
        if (q != null) queueData.push({ timestamp: localStamp(dateStr, sec), queueTime: q, queueCount: Math.round(q * 2), emaRate: 0 });
      }
    });
    out[String(restroom.restroomId)] = { totalEntrances: cin, totalExits: cout, data, queueData };
  });
  return out;
};

// 'get-facilities-restrooms-location-current-data-dashboard-v2' (Locations card): each restroom's
// entries today and latest queue; no queue reading in the last minute means inactive (reads 0)
const buildCurrent = (currentLocation) => {
  const date = testSchedule(currentLocation.timeZone)[1].dates[0].date;
  const ctx = gameContext(currentLocation.timeZone, date);
  const daily = buildDaily(currentLocation, date);
  return (currentLocation.restrooms || []).map((restroom) => {
    const d = daily[String(restroom.restroomId)] || { totalEntrances: 0, totalExits: 0, queueData: [] };
    const last = d.queueData[d.queueData.length - 1];
    const lastSec = last ? Math.round((new Date(last.timestamp) - new Date(`${date}T00:00:00`)) / 1000) : null;
    const active = last != null && ctx.endSec - lastSec <= 60;
    return {
      restroomId: restroom.restroomId,
      sectionId: restroom.sectionId,
      entries: d.totalEntrances,
      exits: d.totalExits,
      queueTime: active ? Math.round(last.queueTime) : 0,
      queueCount: active ? last.queueCount : 0,
      sensorActive: active,
    };
  });
};

// Drop-in replacement for getQueuesAPI. Serves the fake facilities routes, defers everything else.
export const getFacilitiesAPI = (currentLocation) => (route, body, callback, token) => {
  if (!USE_DUMMY_FACILITIES_DATA || !currentLocation) return getQueuesAPI(route, body, callback, token);
  const respond = (data) => setTimeout(() => callback(data), 150);
  switch (route) {
    case 'get-location-current-schedule':
      return respond(testSchedule(currentLocation.timeZone));
    case 'get-facilities-restrooms-location-daily-data-v2':
      return respond(buildDaily(currentLocation, body.date));
    case 'get-facilities-restrooms-location-current-data-dashboard-v2':
      return respond(buildCurrent(currentLocation));
    default:
      return getQueuesAPI(route, body, callback, token);
  }
};
